'use client';
import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import gsap from 'gsap';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

// Animated counter hook
function useCounter(target: number, duration: number = 2000, start: boolean = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number | null = null;
    const step = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [target, duration, start]);
  return count;
}

const features = [
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>,

  title: 'BESS / ESS',
  desc: 'Lithium energy storage from compact 1 kWh packs for UPS and inverter backup, to 250 kW+ systems for off-grid solar, DG replacement, and commercial storage.',
  link: '/bess-ess',
  linkText: 'Explore BESS / ESS'
},
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
      </svg>,

  title: 'BMS (Battery Management System)',
  desc: 'Custom firmware, algorithms, and system configuration for energy storage. Hardware and software BMS solutions tailored to your cell chemistry and pack design.',
  link: '/bms-software',
  linkText: 'Learn About BMS'
},
{
  icon:
  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
      </svg>,

  title: 'Custom Battery Packs',
  desc: 'Design and manufacture custom lithium battery packs for any application. From 12V UPS replacements to high-voltage industrial packs with full BMS integration.',
  link: '/batteries',
  linkText: 'Design Your Pack'
}];


const bmsFeatures = [
{ icon: '⚡', title: 'Real-Time Sensing', desc: 'Voltage, current, temperature monitored 24/7 across every cell' },
{ icon: '🧠', title: 'AI Processing Core', desc: 'Predictive algorithms analyze patterns & optimize in real-time' },
{ icon: '📊', title: 'Optimized Output', desc: 'Maximum efficiency, extended life, cost savings' },
{ icon: '⚖️', title: 'Smart Cell Balancing', desc: 'Active balancing ensures every cell performs at peak capacity' },
{ icon: '🔍', title: 'Predictive Health Monitoring', desc: 'Machine learning predicts degradation patterns before failures occur' },
{ icon: '🔋', title: 'Adaptive Charging', desc: 'Dynamic charge profiles adapt to usage patterns and cell chemistry' },
{ icon: '📱', title: 'Energy Analytics Dashboard', desc: 'Real-time insights, historical trends, and actionable recommendations' },
{ icon: '🛡️', title: 'Multi-Layer Protection', desc: 'Over-voltage, under-voltage, overcurrent, and thermal protection' },
{ icon: '🌐', title: 'OTA Updates', desc: 'Remote firmware updates keep your system optimized with latest algorithms' }];


const whoWeServe = [
{
  icon:
  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-2 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
      </svg>,

  title: 'Industrial & Factory Owners',
  desc: 'Reliable energy backup for CNC machines, PLCs, server rooms, and cold storage. Eliminate downtime with intelligent BMS protection.'
},
{
  icon:
  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>,

  title: 'IT & Facility Managers',
  desc: 'Replace failing lead-acid UPS batteries with long-lasting lithium packs. Zero maintenance, 3x lifespan, 50% smaller footprint.'
},
{
  icon:
  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
      </svg>,

  title: 'Solar & BESS Integrators',
  desc: 'Complete BMS configuration, premium cells, and technical support for energy storage projects from 5 kW to 250 kW.'
},
{
  icon:
  <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>,

  title: 'Battery & EV OEMs',
  desc: 'Custom pack design, BMS integration, and production-grade quality control for battery assemblers and EV manufacturers.'
}];


const whyAgilas = [
{
  icon: '🔩',
  title: 'Precision Manufacturing',
  desc: 'State-of-the-art assembly with automated cell matching, spot welding, and 100% QC testing on every pack.'
},
{
  icon: '🧬',
  title: 'Cell Chemistry Expertise',
  desc: 'Deep knowledge in LFP, NMC, and NCA chemistries. We select the right cell for your application and operating conditions.'
},
{
  icon: '💻',
  title: 'Integrated BMS',
  desc: 'Every pack ships with a fully configured BMS. Protection thresholds, balancing, and communication protocols set for your system.'
},
{
  icon: '🤝',
  title: 'End-to-End Support',
  desc: 'From system design to deployment and after-sales service. One partner for hardware, BMS, and technical support.'
}];


export default function HomePage() {
  const statsRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const [statsVisible, setStatsVisible] = useState(false);

  const projectsCount = useCounter(200, 2000, statsVisible);
  const kwhCount = useCounter(5000, 2000, statsVisible);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {if (entry.isIntersecting) setStatsVisible(true);},
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  // Scroll animation
  useEffect(() => {
    const elements = document.querySelectorAll('.animate-on-scroll, .animate-on-scroll-left, .animate-on-scroll-right');
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    elements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  // GSAP Hero Animations
  useEffect(() => {
    if (!heroRef.current) return;

    const ctx = gsap.context(() => {
      // Animate hero text
      gsap.fromTo('.hero-title-line', 
        { opacity: 0, y: 60, skewY: 2 },
        { opacity: 1, y: 0, skewY: 0, duration: 1, stagger: 0.15, ease: 'power3.out', delay: 0.2 }
      );

      gsap.fromTo('.hero-subtitle',
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out', delay: 0.8 }
      );

      gsap.fromTo('.hero-cta',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: 1.0 }
      );

      gsap.fromTo('.hero-stats',
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, ease: 'power2.out', delay: 1.2 }
      );

      // Animate SVG battery illustration
      gsap.fromTo('.hero-battery-svg',
        { opacity: 0, scale: 0.8, x: 50 },
        { opacity: 1, scale: 1, x: 0, duration: 1.2, ease: 'power3.out', delay: 0.5 }
      );

      // Animate circuit paths
      gsap.fromTo('.circuit-path',
        { strokeDashoffset: 1000 },
        { strokeDashoffset: 0, duration: 2, stagger: 0.3, ease: 'power1.inOut', delay: 0.8 }
      );

      // Floating battery cells
      gsap.to('.float-cell', {
        y: -12,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
        stagger: { each: 0.3 }
      });

      // Energy particles flowing
      gsap.fromTo('.energy-dot',
        { x: -20, opacity: 0 },
        { x: 200, opacity: 0, duration: 3, repeat: -1, ease: 'none', stagger: 0.5,
          keyframes: [
            { x: -20, opacity: 0 },
            { x: 80, opacity: 1 },
            { x: 200, opacity: 0 }
          ]
        }
      );

      // Pulse on the BMS chip
      gsap.to('.bms-pulse', {
        boxShadow: '0 0 30px rgba(230,0,0,0.4)',
        duration: 1.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut'
      });

      // Manufacturing assembly animation
      gsap.fromTo('.assembly-arm',
        { rotation: -15, transformOrigin: 'top center' },
        { rotation: 15, duration: 2, repeat: -1, yoyo: true, ease: 'sine.inOut' }
      );

      // Welding spark effect
      gsap.to('.weld-spark', {
        opacity: 1,
        scale: 1.5,
        duration: 0.3,
        repeat: -1,
        repeatDelay: 1.5,
        yoyo: true,
        ease: 'power2.out'
      });

      // Conveyor belt cells moving
      gsap.to('.conveyor-cell', {
        x: 300,
        duration: 4,
        repeat: -1,
        ease: 'none',
        stagger: { each: 1 }
      });

    }, heroRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      <Navbar />

      {/* Hero Section — GSAP Animated */}
      <section ref={heroRef} className="hero-light relative min-h-screen flex items-center overflow-hidden">
        {/* Animated circuit grid background */}
        <div className="absolute inset-0 circuit-bg opacity-60" />

        {/* Animated SVG circuit lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 1440 900" fill="none" preserveAspectRatio="xMidYMid slice">
          <path className="circuit-path" d="M0 450 H300 V200 H600 V400 H900 V150 H1200 V450 H1440" stroke="#E60000" strokeWidth="1" strokeDasharray="1000" opacity="0.12" />
          <path className="circuit-path" d="M0 600 H200 V700 H500 V500 H800 V650 H1100 V400 H1440" stroke="#E60000" strokeWidth="1" strokeDasharray="1000" opacity="0.08" />
          <path className="circuit-path" d="M0 300 H150 V100 H400 V350 H700 V200 H1000 V350 H1440" stroke="#1A1A1A" strokeWidth="0.5" strokeDasharray="1000" opacity="0.06" />
          {/* Junction nodes */}
          <circle cx="300" cy="200" r="4" fill="#E60000" opacity="0.15" />
          <circle cx="600" cy="400" r="4" fill="#E60000" opacity="0.15" />
          <circle cx="900" cy="150" r="4" fill="#E60000" opacity="0.15" />
          <circle cx="1200" cy="450" r="4" fill="#E60000" opacity="0.15" />
          {/* Energy dots that flow along circuits */}
          <circle className="energy-dot" cx="0" cy="450" r="3" fill="#E60000" />
          <circle className="energy-dot" cx="0" cy="600" r="2.5" fill="#FF3333" />
          <circle className="energy-dot" cx="0" cy="300" r="2" fill="#1A1A1A" />
        </svg>

        {/* Large Battery Manufacturing SVG — right side */}
        <div className="hero-battery-svg absolute right-0 top-1/2 -translate-y-1/2 hidden lg:block w-[550px] xl:w-[650px] pointer-events-none" style={{ right: '-2%' }}>
          <svg viewBox="0 0 600 700" fill="none" xmlns="http://www.w3.org/2000/svg">
            {/* Factory frame / background housing */}
            <rect x="60" y="40" width="480" height="620" rx="32" fill="url(#factoryBg)" stroke="#E60000" strokeWidth="1" opacity="0.08" />

            {/* Manufacturing Assembly Line — conveyor belt */}
            <rect x="80" y="560" width="440" height="16" rx="8" fill="#E60000" opacity="0.06" />
            <line x1="80" y1="560" x2="520" y2="560" stroke="#E60000" strokeWidth="1.5" opacity="0.15" strokeDasharray="8,4" />
            {/* Conveyor rollers */}
            {[120, 200, 280, 360, 440].map((x, i) => (
              <circle key={`roller-${i}`} cx={x} cy="568" r="5" fill="#1A1A1A" opacity="0.08" />
            ))}
            {/* Moving cells on conveyor */}
            <g opacity="0.5">
              <rect className="conveyor-cell" x="-80" y="520" width="50" height="35" rx="6" fill="url(#cellGrad)" opacity="0.4" />
              <rect className="conveyor-cell" x="-220" y="520" width="50" height="35" rx="6" fill="url(#cellGrad)" opacity="0.4" />
              <rect className="conveyor-cell" x="-360" y="520" width="50" height="35" rx="6" fill="url(#cellGrad)" opacity="0.4" />
            </g>

            {/* Robotic Assembly Arm */}
            <g className="assembly-arm">
              <rect x="420" y="40" width="14" height="180" fill="#1A1A1A" opacity="0.12" rx="7" />
              <circle cx="427" cy="220" r="14" fill="#1A1A1A" opacity="0.15" />
              <path d="M413 220 L395 255 M441 220 L459 255" stroke="#1A1A1A" strokeWidth="4" strokeLinecap="round" opacity="0.2" />
              <circle className="weld-spark" cx="395" cy="255" r="5" fill="#E60000" opacity="0" />
              <circle className="weld-spark" cx="459" cy="255" r="4" fill="#FF3333" opacity="0" />
            </g>

            {/* Main Battery Pack Body */}
            <rect x="140" y="100" width="320" height="420" rx="28" fill="url(#battBodyGrad)" stroke="#E60000" strokeWidth="1.5" opacity="0.12" />
            {/* Terminal cap */}
            <rect x="220" y="70" width="160" height="32" rx="10" fill="#1A1A1A" opacity="0.08" />
            <text x="300" y="91" textAnchor="middle" fill="#1A1A1A" fontSize="12" fontWeight="700" opacity="0.4">+ —</text>

            {/* Energy fill bar */}
            <rect className="float-cell" x="160" y="200" width="280" height="300" rx="20" fill="url(#energyFillGrad)" opacity="0.08" />

            {/* Cell grid — 5 rows × 4 cols */}
            {[220, 280, 340, 400, 460].map((y, row) => (
              <g key={`row-${row}`} className="float-cell">
                {[180, 240, 300, 360].map((x, col) => (
                  <g key={`cell-${row}-${col}`}>
                    <rect x={x} y={y} width="45" height="45" rx="8" fill="#E60000" opacity={0.05 + row * 0.02} />
                    <rect x={x + 15} y={y + 12} width="15" height="21" rx="3" fill="#E60000" opacity={0.08 + row * 0.015} />
                  </g>
                ))}
              </g>
            ))}

            {/* BMS Chip — center top */}
            <rect className="bms-pulse" x="210" y="140" width="180" height="50" rx="12" fill="#E60000" opacity="0.06" style={{ borderRadius: '12px' }} />
            <text x="300" y="170" textAnchor="middle" fill="#E60000" fontSize="13" fontWeight="700" opacity="0.4">BMS CONTROLLER</text>

            {/* Connection traces from BMS */}
            <line x1="210" y1="165" x2="140" y2="165" stroke="#E60000" strokeWidth="1" opacity="0.12" />
            <line x1="390" y1="165" x2="460" y2="165" stroke="#E60000" strokeWidth="1" opacity="0.12" />
            <line x1="300" y1="190" x2="300" y2="215" stroke="#E60000" strokeWidth="1" opacity="0.1" strokeDasharray="3,3" />
            {/* BMS trace nodes */}
            <circle cx="140" cy="165" r="4" fill="#E60000" opacity="0.15" />
            <circle cx="460" cy="165" r="4" fill="#E60000" opacity="0.15" />

            {/* Voltage / temp monitoring lines */}
            <path d="M140 165 L100 250 L100 400" stroke="#E60000" strokeWidth="0.8" opacity="0.1" strokeDasharray="4,4" />
            <path d="M460 165 L500 250 L500 400" stroke="#1A1A1A" strokeWidth="0.8" opacity="0.08" strokeDasharray="4,4" />

            {/* Energy indicators — pulsing */}
            <circle cx="100" cy="400" r="8" fill="#E60000" opacity="0.1" className="energy-pulse" />
            <circle cx="500" cy="400" r="8" fill="#1A1A1A" opacity="0.08" className="energy-pulse" />
            <circle cx="100" cy="250" r="5" fill="#E60000" opacity="0.12" className="energy-pulse" />
            <circle cx="500" cy="250" r="5" fill="#1A1A1A" opacity="0.08" className="energy-pulse" />

            {/* Output energy flow line */}
            <path d="M300 520 Q300 590 520 610" fill="none" stroke="#E60000" strokeWidth="2" opacity="0.15" strokeDasharray="6,4" />
            <path d="M300 520 Q300 590 80 610" fill="none" stroke="#1A1A1A" strokeWidth="1" opacity="0.08" strokeDasharray="6,4" />

            {/* Labels */}
            <text x="300" y="640" textAnchor="middle" fill="#1A1A1A" fontSize="11" fontWeight="600" opacity="0.3">AGILAS SYSTEMS — LITHIUM PACK</text>

            <defs>
              <linearGradient id="factoryBg" x1="300" y1="40" x2="300" y2="660" gradientUnits="userSpaceOnUse">
                <stop stopColor="#fff5f5" />
                <stop offset="1" stopColor="#fee2e2" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="battBodyGrad" x1="300" y1="100" x2="300" y2="520" gradientUnits="userSpaceOnUse">
                <stop stopColor="#fff5f5" />
                <stop offset="1" stopColor="#fecaca" stopOpacity="0.3" />
              </linearGradient>
              <linearGradient id="energyFillGrad" x1="300" y1="200" x2="300" y2="500" gradientUnits="userSpaceOnUse">
                <stop stopColor="#E60000" />
                <stop offset="1" stopColor="#FF3333" stopOpacity="0.2" />
              </linearGradient>
              <linearGradient id="cellGrad" x1="0" y1="0" x2="0" y2="35" gradientUnits="objectBoundingBox">
                <stop stopColor="#E60000" />
                <stop offset="1" stopColor="#CC0000" stopOpacity="0.5" />
              </linearGradient>
            </defs>
          </svg>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
          <div className="max-w-2xl">
            {/* Label */}
            <div className="section-label mb-6 hero-title-line">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Precision Lithium Manufacturing
            </div>

            {/* Heading */}
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold leading-tight mb-6">
              <span className="hero-title-line block text-slate-900">Advanced &amp; Reliable</span>
              <span className="hero-title-line block gradient-text-green">Lithium Battery</span>
              <span className="hero-title-line block text-slate-900">Manufacturing</span>
            </h1>

            <p className="hero-subtitle text-slate-600 text-lg leading-relaxed mb-8">
              Premium lithium BESS from 1 kWh to 250 kW for UPS, inverters, off-grid solar, and commercial backup.
              Paired with intelligent BMS engineered for maximum performance and safety.
            </p>

            {/* CTAs */}
            <div className="hero-cta flex flex-wrap gap-4 mb-12">
              <Link href="/bess-ess" className="btn-primary text-base px-8 py-3">
                Explore BESS / ESS
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
              <Link href="/batteries" className="btn-secondary text-base px-8 py-3">
                Design Your Battery Pack
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </Link>
            </div>

            {/* Stats */}
            <div ref={statsRef} className="hero-stats flex flex-wrap gap-6">
              <div>
                <div className="text-3xl font-bold text-slate-900">
                  {statsVisible ? `${projectsCount}+` : '0+'}
                </div>
                <div className="text-slate-500 text-sm mt-1">Projects Delivered</div>
              </div>
              <div className="stat-divider">
                <div className="text-3xl font-bold text-slate-900">
                  {statsVisible ? `${kwhCount}+` : '0+'}
                </div>
                <div className="text-slate-500 text-sm mt-1">kWh Deployed</div>
              </div>
              <div className="stat-divider">
                <div className="text-3xl font-bold text-slate-900 flex items-center gap-2">
                  &lt;3.5 yrs
                </div>
                <div className="text-slate-500 text-sm mt-1">Average ROI</div>
              </div>
            </div>
          </div>

        </div>

        {/* Fit and Forget badge */}
        <div className="absolute bottom-24 right-12 hidden lg:flex items-center gap-3 bg-white/90 backdrop-blur border border-primary/20 rounded-2xl px-5 py-3 float-anim shadow-lg z-20" style={{ animationDelay: '1s' }}>
          <div className="w-10 h-10 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div>
            <span className="text-primary font-semibold">Fit</span>
            <span className="text-slate-900"> and Forget</span>
            <div className="text-slate-500 text-xs">Zero maintenance lithium packs</div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <div className="text-slate-400 text-xs tracking-widest uppercase">Scroll</div>
          <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </section>

      {/* Lithium Cell Showcase */}
      <section className="py-16 bg-slate-50 border-y border-slate-200 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Our Cell Portfolio</div>
            <h2 className="text-2xl font-bold text-slate-900">Premium Lithium Cell Technologies</h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_16ff42bd1-1767728458618.png", alt: 'Cylindrical 18650 lithium cells arranged in rows', label: '18650 Cylindrical', chem: 'NMC / LFP' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1bee22384-1767795971645.png", alt: 'Large format prismatic lithium iron phosphate cells', label: 'Prismatic LFP', chem: 'LiFePO₄' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_14b410ac4-1767795972484.png", alt: 'Cylindrical 21700 lithium cells for high energy density applications', label: '21700 Cylindrical', chem: 'NMC High-Energy' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_13396ba4c-1764710167910.png", alt: 'Pouch lithium polymer cells for custom battery pack design', label: 'Pouch Cells', chem: 'LiPo / NMC' }].
            map((cell, i) =>
            <div key={i} className="card-dark overflow-hidden group animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="aspect-video overflow-hidden">
                  <img
                  src={cell.img}
                  alt={cell.alt}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                
                </div>
                <div className="p-4">
                  <div className="text-slate-900 font-semibold text-sm">{cell.label}</div>
                  <div className="text-primary text-xs mt-1">{cell.chem}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-24 relative">
        <div className="absolute inset-0 circuit-bg opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="section-label mx-auto mb-4">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
              What We Do
            </div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Complete Energy Storage <span className="gradient-text-green">Solutions</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Three integrated verticals: precision hardware manufacturing, intelligent BMS, and custom battery pack design.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {features.map((f, i) =>
            <div
              key={i}
              className="card-dark p-8 group cursor-pointer animate-on-scroll"
              style={{ animationDelay: `${i * 0.15}s` }}>
              
                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mb-6 group-hover:bg-primary/20 transition-colors">
                  {f.icon}
                </div>
                <h3 className="text-slate-900 font-bold text-lg mb-3">{f.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">{f.desc}</p>
                <Link
                href={f.link}
                className="flex items-center gap-2 text-primary text-sm font-medium group-hover:gap-3 transition-all">
                
                  {f.linkText}
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Battery Manufacturing Showcase */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div className="animate-on-scroll-left">
              <div className="section-label mb-6">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                </svg>
                Intelligent BMS
              </div>
              <h2 className="text-4xl font-bold text-slate-900 mb-4">
                Intelligent BMS &amp; <span className="gradient-text-green">AI Optimization</span>
              </h2>
              <p className="text-slate-600 mb-8 leading-relaxed">
                Our AI-powered Battery Management System maximizes efficiency, extends lifespan, and delivers real-time insights for every pack we manufacture.
              </p>

              {/* BMS flow */}
              <div className="flex items-center gap-4 mb-8">
                {[
                { label: 'Real-Time Sensing', icon: '📡' },
                { label: 'AI Processing', icon: '🧠' },
                { label: 'Optimized Output', icon: '⚡' }].
                map((step, i) =>
                <React.Fragment key={i}>
                    <div className="flex flex-col items-center gap-2 flex-1">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-xl">
                        {step.icon}
                      </div>
                      <div className="text-xs text-slate-500 text-center">{step.label}</div>
                    </div>
                    {i < 2 &&
                  <svg className="w-6 h-6 text-primary flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                  }
                  </React.Fragment>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                {bmsFeatures.slice(3, 7).map((f, i) =>
                <div key={i} className="flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-200">
                    <span className="text-xl">{f.icon}</span>
                    <div>
                      <div className="text-slate-900 text-sm font-semibold">{f.title}</div>
                      <div className="text-slate-500 text-xs mt-1">{f.desc}</div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="animate-on-scroll-right">
              <div className="relative">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_13fdf79f9-1765300594276.png"
                  alt="Battery management system circuit board with electronic components"
                  className="w-full rounded-2xl border border-slate-200 shadow-lg" />
                
                <div className="absolute inset-0 rounded-2xl" style={{ background: 'linear-gradient(135deg, rgba(230,0,0,0.05) 0%, transparent 60%)' }} />
                {/* Overlay stats */}
                <div className="absolute bottom-4 left-4 right-4 grid grid-cols-3 gap-2">
                  {[
                  { val: '6000+', label: 'Cycle Life' },
                  { val: '±1%', label: 'SOC Accuracy' },
                  { val: '100ms', label: 'Response Time' }].
                  map((s, i) =>
                  <div key={i} className="bg-white/90 backdrop-blur border border-slate-200 rounded-xl p-3 text-center shadow-sm">
                      <div className="text-primary font-bold text-lg">{s.val}</div>
                      <div className="text-slate-500 text-xs">{s.label}</div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="py-24 relative">
        <div className="absolute inset-0 hex-pattern opacity-60" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Industries</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Who We <span className="gradient-text-green">Serve</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Our solutions are built for organizations driving the energy transition across India.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoWeServe.map((item, i) =>
            <div
              key={i}
              className="card-dark p-6 text-center group animate-on-scroll"
              style={{ animationDelay: `${i * 0.1}s` }}>
              
                <div className="w-16 h-16 rounded-2xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary mx-auto mb-4 group-hover:bg-primary/20 transition-colors">
                  {item.icon}
                </div>
                <h3 className="text-slate-900 font-bold mb-3">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Battery Images Gallery */}
      <section className="py-16 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 animate-on-scroll">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">Our Manufacturing Capabilities</h2>
            <p className="text-slate-600">Precision-engineered lithium battery packs for every application</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {[
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_10a8adfe2-1768070056140.png", alt: 'Lithium battery pack assembly with 18650 cells in custom configuration', label: 'Custom Pack Assembly' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1bee22384-1767795971645.png", alt: 'Large format LFP prismatic cells for industrial energy storage systems', label: 'Industrial BESS' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_138ae4584-1765684972052.png", alt: 'Electric vehicle battery pack with BMS electronics and cooling system', label: 'EV Battery Packs' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1b016ae72-1778658397021.png", alt: 'BMS circuit board with protection electronics and communication modules', label: 'BMS Electronics' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1b95c79e9-1776540708300.png", alt: 'Solar energy storage system with lithium battery bank and inverter', label: 'Solar Storage' },
            { img: "https://img.rocket.new/generatedImages/rocket_gen_img_1c462dbb3-1772116744411.png", alt: 'Quality control testing of lithium battery cells in manufacturing facility', label: 'Quality Testing' }].
            map((item, i) =>
            <div key={i} className="relative group overflow-hidden rounded-2xl border border-slate-200 shadow-sm animate-on-scroll" style={{ animationDelay: `${i * 0.1}s` }}>
                <img
                src={item.img}
                alt={item.alt}
                className="w-full aspect-video object-cover group-hover:scale-105 transition-transform duration-500" />
              
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-transparent to-transparent" />
                <div className="absolute bottom-3 left-3">
                  <span className="badge badge-green">{item.label}</span>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Why Agilas */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-on-scroll">
            <div className="section-label mx-auto mb-4">Why Choose Us</div>
            <h2 className="text-4xl font-bold text-slate-900 mb-4">
              Why <span className="gradient-text-green">AGILAS SYSTEMS</span>
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              We combine deep technical expertise with precision manufacturing to deliver complete lithium energy solutions.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {whyAgilas.map((item, i) =>
            <div
              key={i}
              className="card-dark p-6 animate-on-scroll"
              style={{ animationDelay: `${i * 0.1}s` }}>
              
                <div className="text-3xl mb-4">{item.icon}</div>
                <h3 className="text-slate-900 font-bold mb-2">{item.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{item.desc}</p>
              </div>
            )}
          </div>

          <div className="text-center animate-on-scroll">
            <Link href="/contact" className="btn-primary text-base px-10 py-4">
              Start a Conversation
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>);

}